/**
 * Pricing Breakdown Component
 * Displays detailed pricing with line items, fees, and total
 */

import { PriceLineItem, PricingDivider } from './PriceLineItem';
import { formatCurrency } from '../../utils/formatCurrency';
import type { CartItem } from '../../types/widget/order';
import type { PricingConfig } from '../../services/provider.service';

interface PricingBreakdownProps {
  item: CartItem;
  pricing: PricingConfig;
  showDisclaimer?: boolean;
  disclaimerText?: string;
}

/**
 * Get base rental price for container from pricing config
 */
function getContainerRentalPrice(
  containerSize: string,
  pricing: PricingConfig
): number {
  return pricing.basePricing?.[containerSize] || 0;
}

/**
 * Get delivery fee based on order type
 * Uses actual pricing config values
 */
function getDeliveryFee(_orderType: string, pricing: PricingConfig): number {
  // Note: Backend no longer distinguishes between pickup/delivery fees
  // All order types use the same deliveryFee
  return pricing.deliveryFee || 0;
}

/**
 * Get disposal fee estimate from pricing config
 * Falls back to general rate if specific material type not found
 */
function getDisposalFeeEstimate(materialType: string, pricing: PricingConfig): number {
  // Try to find exact match or fall back to general waste rate
  return pricing.disposalFees?.[materialType] || pricing.disposalFees?.['general_waste'] || pricing.disposalFees?.['general'] || 0;
}

/**
 * Get enhanced tooltip text for disposal fees
 * Provides detailed explanation with material type and rate information
 */
function getDisposalTooltipText(materialType: string, rate: number): string {
  const materialLabel = materialType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  if (rate > 0) {
    return `Estimated disposal cost for ${materialLabel}. Based on ${formatCurrency(rate)}/ton average. Typical load: 2-4 tons. Final cost determined after weighing at facility.`;
  }

  return `Estimated disposal cost for ${materialLabel}. Final cost based on actual weight and material composition measured at facility.`;
}

/**
 * Get order-type-specific disclaimer text
 * Provides context-appropriate pricing information
 */
function getDisclaimerText(orderType: string): string {
  const baseText = 'Final pricing based on actual weight and volume.';

  switch (orderType) {
    case 'delivery':
      return `${baseText} You will receive a final invoice after container pickup and waste processing.`;
    case 'swap':
      return `${baseText} Swap fee includes container exchange. You will receive a final invoice after waste processing.`;
    case 'pickup':
      return `${baseText} Pickup fee includes container retrieval. You will receive a final invoice after waste processing.`;
    default:
      return `${baseText} You will receive a final invoice after service completion.`;
  }
}

/**
 * Calculate tax if tax rate is provided
 * Returns 0 if no tax rate available
 */
function calculateTax(subtotal: number, taxRate?: number): number {
  if (!taxRate || taxRate <= 0) {
    return 0;
  }
  return subtotal * taxRate;
}

/**
 * PricingBreakdown component
 * Shows all costs clearly with tooltips for fees
 */
export function PricingBreakdown({
  item,
  pricing,
  showDisclaimer = true,
  disclaimerText,
}: PricingBreakdownProps) {
  // Calculate pricing components
  const containerRental = getContainerRentalPrice(item.containerSize, pricing);
  const deliveryFee = getDeliveryFee(item.orderType, pricing);
  const disposalEstimate = getDisposalFeeEstimate(item.materialType, pricing);

  // Calculate subtotal and total
  const subtotal = containerRental + deliveryFee + disposalEstimate;
  const tax = calculateTax(subtotal, 'taxRate' in pricing ? (pricing as PricingConfig & { taxRate?: number }).taxRate : undefined);
  const total = subtotal + tax;

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">Pricing Breakdown</h3>

      {/* Line items */}
      <div className="space-y-2">
        {/* Container rental */}
        {containerRental > 0 && (
          <PriceLineItem
            label={`${item.containerSize} Container Rental`}
            amount={containerRental}
            tooltip="Rental period includes up to 14 days on-site. Extended rental fees may apply for longer periods. Includes delivery and pickup."
          />
        )}

        {/* Delivery fee */}
        {deliveryFee > 0 && (
          <PriceLineItem
            label={
              item.orderType === 'swap'
                ? 'Swap Fee'
                : item.orderType === 'delivery'
                ? 'Delivery Fee'
                : 'Pickup Fee'
            }
            amount={deliveryFee}
            tooltip={
              item.orderType === 'swap'
                ? 'One-time fee to swap your full container for an empty one.'
                : 'One-time delivery and pickup charge for container service.'
            }
          />
        )}

        {/* Disposal fee estimate */}
        <PriceLineItem
          label="Disposal Fee (estimate)"
          amount={disposalEstimate}
          tooltip={getDisposalTooltipText(item.materialType, disposalEstimate)}
        />
      </div>

      {/* Subtotal */}
      <PricingDivider />
      <PriceLineItem label="Subtotal" amount={subtotal} isSubtotal />

      {/* Tax (if applicable) */}
      {tax > 0 && <PriceLineItem label="Tax" amount={tax} />}

      {/* Total */}
      <PricingDivider />
      <PriceLineItem label="Estimated Total" amount={total} isTotal />

      {/* Disclaimer */}
      {showDisclaimer && (
        <p
          role="status"
          aria-live="polite"
          className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500 leading-relaxed"
        >
          {disclaimerText || getDisclaimerText(item.orderType)}
        </p>
      )}
    </div>
  );
}
